/**
 * Settings: the body of the setup sheet's `settings` mode, in five sections.
 *
 * Extracted from `Setup.tsx` rather than made a second dialog. There is no new
 * store state here, no new modal and no new Escape rung — `setupOpen` still names
 * the one open setup surface, and a section is a place within it. Two files
 * because one had become unreadable, not because there are two sheets.
 *
 * WHY A TABLIST AND NOT A LONG SCROLL. `useModalFocus` wraps Tab across the whole
 * dialog. Six sections of controls inside one wrap makes Tab useless: reaching the
 * source switches means walking past every key field and every routing select, and
 * Shift+Tab from the top lands on the last switch. The tablist fixes both ends —
 * the nav collapses to ONE tab stop (roving `tabIndex`, arrows to move), and only
 * the inactive panels are `display: none`, which takes them out of the tab order
 * just as unmounting them would, so the wrap stays as short as the section is.
 * They stay MOUNTED for a reason that outranks tidiness — see the panels below.
 *
 * WHAT THIS SURFACE DELIBERATELY DOES NOT EXPLAIN. The brief was a field for the
 * key and a dropdown for the model, not an essay on what each model is for — so
 * the per-model `note` stays off Providers; the first run's "Other models" is
 * where every row keeps its note and its search statement. Routing is the
 * exception, and the reason is where it went: the first run no longer carries a
 * routing control at all, so `ROLE_NOTE` — one line naming the buttons each select
 * re-points — moved here under its select, with a one-line preamble. What stayed
 * besides is not description: the key shape warning, the hostname disclosure,
 * `baseUrlProblem`, `roleWarning` and the routing refusal sentence, `searchNote`'s
 * `'none'` branch, the endpoint row's note (which API it needs) and the
 * model-stamp note are each the app stating what it will REFUSE to do, and
 * deleting any of them turns a refusal into a silence. Invariants 3 and 4 are
 * literally the last two. Every one is said once, beside the control it is about,
 * in the words a user would use; the reasoning behind them is in docs/guide.md.
 *
 * NO USAGE TABLE, and that is a data decision rather than a layout one. `Spend`
 * merges on model only, and `session.ops` is sliced to 200 on the way to disk — so
 * a per-purpose table would under-report and never say it was doing so, and doing
 * it honestly is a SCHEMA bump plus a migration. Nothing in here prints money.
 */

import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type KeyboardEvent,
  type ReactNode,
  type SetStateAction,
} from 'react'
import {
  BOOT_MODEL_UNKNOWN,
  activeEntry,
  entryFor,
  runAuditor,
  seededTune,
  useStore,
  type SettingsSection,
  type Theme,
  type TuneDefaults,
} from '../core/store'
import { projectMoveUsd } from '../engine/run'
import {
  DEFAULT_EFFORT,
  EFFORT_LABEL,
  EFFORT_LADDER,
  EFFORT_WORK_LABEL,
  JUDGES,
  JUDGE_ENDPOINT,
  PROVIDERS,
  ROLES,
  EFFICIENT_QUALITY,
  ROLE_LABEL,
  ROLE_NOTE,
  presetApplied,
  SELECTABLE_MODELS,
  effortCeilingNote,
  effortNote,
  CROSS_EXAM_NEEDS_SECOND_KEY,
  findModel,
  houseModelId,
  keyCardProviders,
  levelFor,
  roleWarning,
  searchNote,
  type Effort,
  type EffortLevels,
  type EffortWork,
  type ModelEntry,
  type ProviderId,
  type Role,
} from '../core/models'
import { LENS_LABEL, TEMPER_LABEL, type Lens, type Temper } from '../core/types'
import { MOVE_ROWS } from '../engine/command'
import { TEMPOS } from '../core/tempo'
import { SOURCES, SOURCE_CATEGORIES, SOURCE_CATEGORY_LABEL } from '../core/sources'
import { SOURCES_DOOR } from '../core/corpus'
import { ProviderKeyCard, type KeyCardResult } from './ProviderKeyCard'
import { Icon } from './components/Icon'

const SECTIONS: ReadonlyArray<{ id: SettingsSection; label: string }> = [
  // Most-likely-reason-you-opened-it first. `providers` leads because the
  // composer's two no-key fallbacks open this sheet, and they must land on a key
  // field rather than somewhere a reader has to navigate away from.
  { id: 'providers', label: 'Providers' },
  { id: 'routing', label: 'Routing' },
  { id: 'defaults', label: 'Defaults' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'sources', label: 'Sources' },
]

export function SettingsPanel({
  pick,
  setPick,
  draftRoles,
  setDraftRoles,
  draftEffort,
  setDraftEffort,
  registerKeyCard,
  onKeyCardReady,
  onSubmit,
}: {
  /** The selected model, as a draft. Committed by the sheet's Save, for the
   *  reason the sheet has always had one: choosing a model decides what the next
   *  pass costs, and that should take a button rather than a keystroke. */
  pick: string
  setPick: Dispatch<SetStateAction<string>>
  draftRoles: Partial<Record<Role, string>>
  setDraftRoles: Dispatch<SetStateAction<Partial<Record<Role, string>>>>
  /** How hard each kind of work thinks, as a draft committed by the same Save
   *  as the routes beside it (D313). */
  draftEffort: EffortLevels
  setDraftEffort: Dispatch<SetStateAction<EffortLevels>>
  registerKeyCard: (p: ProviderId, commit: (() => KeyCardResult) | null) => void
  onKeyCardReady: (p: ProviderId, ready: boolean | null) => void
  onSubmit: () => void
}) {
  const section = useStore((s) => s.setupSection)
  const setSetup = useStore((s) => s.setSetup)
  const keys = useStore((s) => s.keys)
  const storedBase = useStore((s) => s.customBase)
  const modelId = useStore((s) => s.modelId)
  const spend = useStore((s) => s.session.spend)

  const tabs = useRef<Array<HTMLButtonElement | null>>([])
  const here = SECTIONS.findIndex((s) => s.id === section)
  const at = here < 0 ? 0 : here

  // Arrow keys move between tabs, which is what a tablist promises and what the
  // single tab stop makes necessary — with a roving tabIndex, Tab can no longer
  // reach the other four.
  const onNavKey = (e: KeyboardEvent) => {
    const n = SECTIONS.length
    const to =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? (at + 1) % n
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? (at + n - 1) % n
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? n - 1
              : -1
    if (to < 0) return
    e.preventDefault()
    setSetup('settings', SECTIONS[to]!.id)
    tabs.current[to]?.focus()
  }

  // The strip is narrower than its five labels on a phone, and the sheet can be
  // opened straight into any section — `setSetup('settings', 'sources')` — so the
  // active tab can start scrolled off the right edge, which reads as a nav that
  // does not contain the thing it is showing. `nearest` in both axes so this can
  // never scroll the dialog body under it.
  useEffect(() => {
    tabs.current[at]?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [at])

  return (
    <>
      {/* Always a scrolling strip, not only under 767px. Five labels fit at the
          sheet's 620px and do not at a phone's 343 — and one shape at every width
          is one thing to reason about, where a media query would give the nav two
          behaviours and no way to test the seam from a component file. */}
      <div
        className="segtabs"
        role="tablist"
        aria-label="Settings sections"
        onKeyDown={onNavKey}
        style={{ overflowX: 'auto', scrollbarWidth: 'none' }}
      >
        {SECTIONS.map((s, i) => (
          <button
            key={s.id}
            ref={(el) => {
              tabs.current[i] = el
            }}
            role="tab"
            id={`setupTab-${s.id}`}
            aria-selected={s.id === section}
            aria-controls={`setupPanel-${s.id}`}
            tabIndex={s.id === section ? 0 : -1}
            onClick={() => setSetup('settings', s.id)}
            style={{ flex: '0 0 auto', padding: '7px 12px' }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* All five panels stay MOUNTED and the inactive ones are hidden, rather
          than one panel swapping its contents. Two reasons, both correctness.

          The key draft lives in `ProviderKeyCard`'s own state and is deliberately
          never seeded from storage, so unmounting Providers destroys it — and
          "paste a key, step over to Routing, press Save" is an ordinary thing to
          do in a sheet that shows Save on every tab. Losing a pasted secret to a
          tab press is not a state this surface may have.

          And `aria-controls` on all five tabs names all five panels, so all five
          have to exist; with one swapping panel, four of those references pointed
          at nothing.

          `hidden` alone is not enough: `.sheet-body` sets `display: flex`, which
          outranks the user agent's `[hidden] { display: none }`. The inline
          display is what actually hides it; `hidden` is what takes it out of the
          accessibility tree and the tab order. */}
      {SECTIONS.map((s) => (
        <div
          key={s.id}
          className="sheet-body scroll"
          role="tabpanel"
          id={`setupPanel-${s.id}`}
          aria-labelledby={`setupTab-${s.id}`}
          hidden={s.id !== section}
          style={s.id === section ? undefined : { display: 'none' }}
        >
          {s.id === 'providers' && (
            <Providers
              pick={pick}
              setPick={setPick}
              keys={keys}
              storedBase={storedBase}
              modelId={modelId}
              showStampNote={spend.length > 0}
              registerKeyCard={registerKeyCard}
              onKeyCardReady={onKeyCardReady}
              onSubmit={onSubmit}
            />
          )}
          {s.id === 'routing' && (
            <RoutingSection
              pick={pick}
              draftRoles={draftRoles}
              setDraftRoles={setDraftRoles}
              draftEffort={draftEffort}
              setDraftEffort={setDraftEffort}
            />
          )}
          {s.id === 'defaults' && <Defaults />}
          {s.id === 'appearance' && <Appearance />}
          {s.id === 'sources' && <Sources />}
        </div>
      ))}
    </>
  )
}

/* ── Providers ────────────────────────────────────────────────────────────
   One card per provider: which of its models runs, and the key that runs it.
   The old flat radio list of every model from every house is gone — it made
   picking a model and connecting its key two separate journeys through the same
   sheet, and it is what made the draft's owner a thing to be inferred. */
function Providers({
  pick,
  setPick,
  keys,
  storedBase,
  modelId,
  showStampNote,
  registerKeyCard,
  onKeyCardReady,
  onSubmit,
}: {
  pick: string
  setPick: Dispatch<SetStateAction<string>>
  keys: Record<ProviderId, string | null>
  storedBase: string
  modelId: string
  showStampNote: boolean
  registerKeyCard: (p: ProviderId, commit: (() => KeyCardResult) | null) => void
  onKeyCardReady: (p: ProviderId, ready: boolean | null) => void
  onSubmit: () => void
}) {
  const entry = findModel(pick) ?? activeEntry({ modelId })
  const hasKey = (p: ProviderId) => (p === 'custom' ? !!storedBase : !!keys[p])
  // The custom row is absent from the picker on the deployed build, so its card
  // is too — a key field for a provider you cannot select is a dead control.
  // The judge has a key and no model rows, so it is never among the first; its
  // card comes last and says what the key is for, because "paste a key for the
  // model you picked" is not the sentence that describes it — and it comes at
  // all only on a build that names an endpoint the page can reach. The API
  // answers no browser, so on the default build there is no card: a key field
  // for a host the page cannot call is a dead control (D107). One list, shared
  // with `askForKey`, so a refusal never singles out a card this does not draw.
  const houses = keyCardProviders(JUDGE_ENDPOINT)
  const providers = houses.filter((p) => !PROVIDERS[p].judge)
  const judges = houses.filter((p) => PROVIDERS[p].judge)
  // A refusal that opened this sheet for a missing key says so on that house's
  // card, more exactly than the line below can — so the line stands down when
  // they would be two sentences about one empty field.
  const askedHere = useStore(
    (s) => s.keyAsk?.why === 'missing' && s.keyAsk.provider === entry.provider,
  )

  return (
    <>
      {!hasKey(entry.provider) && !askedHere && (
        // `role="status"` and not an alert: arriving without a key is the ordinary
        // first visit, not an error. It is announced because the two composer
        // fallbacks land here for exactly this reason and should say so on arrival
        // rather than leaving a reader to find the empty field.
        <p className="gnote gnote--warn" role="status">
          No key for {PROVIDERS[entry.provider].name} — the field below is empty.
        </p>
      )}
      {BOOT_MODEL_UNKNOWN && (
        <p className="gnote gnote--warn">
          The model this workspace was set to is not in this build. Nothing has run on the
          replacement below yet — choose one before the next pass.
        </p>
      )}
      {showStampNote && (
        // Invariant 3 said out loud. Only once anything has actually run: before
        // that there is nothing on the canvas for the sentence to be about.
        <p className="gnote">
          Which model runs the next pass. Nodes already on the canvas keep the name of the model
          that produced them.
        </p>
      )}

      {providers.map((p) => {
        const models = SELECTABLE_MODELS.filter((m) => m.provider === p)
        const owns = models.some((m) => m.id === pick)
        // The row this card's select is showing. When the selection belongs to
        // another house this is simply that house's first model — and choosing
        // one here is what moves the selection, which the ACTIVE chip tracks.
        const sel = models.find((m) => m.id === pick) ?? models[0]!
        const ceiling = effortCeilingNote(sel)
        // `efforts` is a contiguous prefix of EFFORT_LADDER by invariant, so the
        // last entry IS the ceiling — which is the whole of that sentence that
        // fits in a chip.
        const top = sel.efforts[sel.efforts.length - 1]
        return (
          <div
            key={p}
            className="field"
            aria-current={owns && !hasKey(p) ? 'true' : undefined}
            style={{ gap: 8 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="eyebrow">{PROVIDERS[p].name.toUpperCase()}</span>
              {owns && <span className="chip">Active</span>}
            </div>
            <select
              className="select"
              value={sel.id}
              aria-label={`${PROVIDERS[p].name} model`}
              onChange={(e) => setPick(e.target.value)}
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
            {/* Only the 'none' branch. The other two describe a capability that is
                present and working; this one says a whole feature turns itself off,
                and a check with no search behind it reads as "nothing found" when
                the truth is "never searched". */}
            {sel.search === 'none' && <p className="gnote">{searchNote(sel)}</p>}
            {/* The endpoint row's note is a requirement rather than a description
                — which API the address must speak, and the local servers that do
                not — so it is said here, where the address is typed. */}
            {PROVIDERS[p].custom && <p className="gnote">{sel.note}</p>}
            {ceiling && top && (
              <span className="chip">Thinking tops out at {EFFORT_LABEL[top]}</span>
            )}
            <ProviderKeyCard
              provider={p}
              activeModelName={owns ? sel.name : undefined}
              register={registerKeyCard}
              onReady={onKeyCardReady}
              onSubmit={onSubmit}
              // Another house's key pasted here moves the selection to that
              // house's card, whose field takes the caret (D335).
              onSwitchHouse={(h) => {
                const id = houseModelId(h)
                if (id) setPick(id)
              }}
            />
          </div>
        )
      })}

      {judges.map((p) => (
        <div key={p} className="field" style={{ gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="eyebrow">{PROVIDERS[p].name.toUpperCase()} · JUDGE</span>
            {keys[p] && <span className="chip">Connected</span>}
          </div>
          {/* What the key buys, in the registry's own sentence, and what its
              absence does — which is nothing. A judge is the one house here that
              is optional in every state: no feature turns off without it, and no
              surface falls back to another model to answer its question. */}
          <p className="gnote">
            {JUDGES[0]!.name} — {JUDGES[0]!.note} Optional: without a key nothing turns off, and
            every answer it gives is stamped and billed under its own name.
          </p>
          <ProviderKeyCard
            provider={p}
            register={registerKeyCard}
            onReady={onKeyCardReady}
            onSubmit={onSubmit}
          />
        </div>
      ))}
    </>
  )
}

/* ── Routing ──────────────────────────────────────────────────────────────
   Invariant 4's user-facing half. Every sentence here is about what the app will
   REFUSE to do, which is why none of it was cut with the rest of the prose.

   The only routing control in the app. The first run used to render this too,
   folded behind a <details> with a paragraph of preamble; it no longer carries
   routing at all, because a newcomer who has not run a pass has nothing to
   re-point, and one routing control is the whole point — two were two places for
   `draftRoles` to be committed from. What the first run taught about routing came
   here with it, cut to what a control needs: one line under the heading saying
   what the tab does, and one `ROLE_NOTE` line under each select naming the
   buttons it re-points. The TAB is the disclosure; wrapping a second one inside it
   left a whole panel showing a single collapsed summary. */
/**
 * How much lower a reasoning move is estimated on the preset's model than on the
 * selected one, at list rates — the one thing the preset changes most, and the
 * reason anybody would press it. The card never said. Computed from the registry
 * rather than written into the copy, because list rates here have moved twice in
 * a month; null when either model has no list rate or the route saves nothing.
 */
export function reasoningSaving(
  pick: string,
  to: string | undefined,
): { pct: number; from: string } | null {
  const from = findModel(pick)
  const target = to ? findModel(to) : undefined
  if (!from?.price || !target?.price || from.id === target.id) return null
  const a = projectMoveUsd(from.price)
  const b = projectMoveUsd(target.price)
  const pct = Math.round((1 - b / a) * 100)
  return pct > 0 ? { pct, from: from.name } : null
}

/**
 * The preset control, above the five selects it writes into.
 *
 * It writes `draftRoles` and nothing else, so a preset is committed by the same
 * Save as a hand-set route and lands in the same `roleModels` — which is what
 * makes it "a transparent set of ordinary role routes" rather than a second
 * routing mechanism. Every route it writes then renders in the selects below,
 * where it can be edited or cleared one at a time like any other.
 *
 * A toggle, and the label is computed from the routes rather than from a stored
 * flag: change one select afterwards and the preset is simply no longer applied,
 * with nothing to fall out of step. Applying OVERWRITES whatever is routed, which
 * is said before the press rather than discovered after it.
 */
function PresetRow({
  pick,
  draftRoles,
  setDraftRoles,
}: {
  pick: string
  draftRoles: Partial<Record<Role, string>>
  setDraftRoles: Dispatch<SetStateAction<Partial<Record<Role, string>>>>
}) {
  const preset = EFFICIENT_QUALITY
  const applied = presetApplied(draftRoles, preset)
  const saving = reasoningSaving(pick, preset.routes.reason)
  // Roles this press would overwrite: routed already, and somewhere else. A count
  // of routes that already agree with the preset would overstate what is at risk.
  const overwrites = ROLES.filter((r) => draftRoles[r] && draftRoles[r] !== preset.routes[r]).length

  // No margins: the enclosing `.field` is a flex column with a gap, so every child
  // here is already its own row. `alignSelf` is the one exception — a stretched
  // flex item would make this a full-width button, which is louder than a control
  // nobody should press by default has any business being.
  return (
    <div className="field">
      <div className="mrow-head">{preset.name}</div>
      <span className="mrow-note">{preset.note}</span>
      <button
        type="button"
        className="btn"
        style={{ alignSelf: 'start' }}
        onClick={() =>
          setDraftRoles((r) => {
            if (!presetApplied(r, preset)) return { ...r, ...preset.routes }
            // Clearing removes the five the preset set, restoring "follow the
            // selection" — the state routing starts in. It cannot restore routes
            // the preset overwrote, which is why the press that overwrites them
            // says so first.
            const next = { ...r }
            for (const role of ROLES) delete next[role]
            return next
          })
        }
      >
        {applied ? 'Clear it' : 'Apply it'}
      </button>
      <span className="mrow-note">
        {/* Why it is opt-in — the evaluation that would make it the default did
            not pass, on a model's scoring rather than a person's (D314) — is in
            docs/running.md; here it is said as what it means for the person
            pressing. */}
        {applied
          ? 'Applied. Clearing it sends all five back to the selected model.'
          : overwrites > 0
            ? `Applying overwrites ${overwrites} role${overwrites === 1 ? '' : 's'} you have already routed.`
            : `${saving ? `Each reasoning move is estimated about ${saving.pct}% lower at list rates than on ${saving.from}. ` : ''}Off unless you apply it: in its one blind comparison, scored by a model rather than a person, reasoning on Sonnet 5 came out about a point lower out of five. It fills in the five routes below, which you can change one at a time.`}
      </span>
    </div>
  )
}

export function RoutingSection({
  pick,
  draftRoles,
  setDraftRoles,
  draftEffort,
  setDraftEffort,
}: {
  pick: string
  draftRoles: Partial<Record<Role, string>>
  setDraftRoles: Dispatch<SetStateAction<Partial<Record<Role, string>>>>
  draftEffort: EffortLevels
  setDraftEffort: Dispatch<SetStateAction<EffortLevels>>
}) {
  const keys = useStore((s) => s.keys)
  const storedBase = useStore((s) => s.customBase)
  const customModel = useStore((s) => s.customModel)
  const routedCount = ROLES.filter((r) => draftRoles[r]).length
  const heading = `Where each kind of work goes, and how hard it thinks${routedCount > 0 ? ` — ${routedCount} routed` : ''}`
  // The model the next cross-examination would get under the routes as drafted,
  // for its row's clamp note — null without a second company's key, and then the
  // row says whose model it will be rather than naming one.
  const auditor = runAuditor({
    modelId: pick,
    roleModels: draftRoles,
    keys,
    customBase: storedBase,
    customModel,
  })

  return (
    <div className="field">
      <span className="eyebrow">{heading}</span>
      <p className="gnote">
        Each kind of work runs on the model selected under Providers unless you choose another here,
        and thinks as hard as you set beside it. More thinking takes longer and draws more. The
        temper changes what a move is asked, never how hard it thinks.
      </p>
      <PresetRow pick={pick} draftRoles={draftRoles} setDraftRoles={setDraftRoles} />
      <div className="route-cols" aria-hidden="true">
        <span>Model</span>
        <span>Thinking</span>
      </div>
      <ul className="mlist route-list">
        {ROLES.map((role) => {
          const routed = draftRoles[role]
          const target = entryFor({ modelId: pick, roleModels: draftRoles }, role)
          const warn = roleWarning(role, target)
          // Asked of the target, not the selection: a role pointed at a house
          // whose key is missing is a feature that will refuse, and saying so
          // here is cheaper than saying it at the pass.
          const lacks = PROVIDERS[target.provider].custom ? !storedBase : !keys[target.provider]
          return (
            <li key={role} style={{ marginTop: 10 }}>
              <label className="mrow-head" htmlFor={`route-${role}`}>
                {ROLE_LABEL[role]}
              </label>
              <div className="route-pair">
                <select
                  id={`route-${role}`}
                  className="select"
                  value={routed ?? ''}
                  onChange={(e) =>
                    setDraftRoles((r) => {
                      const next = { ...r }
                      if (e.target.value) next[role] = e.target.value
                      else delete next[role]
                      return next
                    })
                  }
                >
                  {/* The empty value is a real state and not a placeholder: it
                      means "follow the selection", and it keeps following it when
                      the selection changes. Pinning the same model by name would
                      look identical today and diverge tomorrow. */}
                  <option value="">Same as selected ({activeEntry({ modelId: pick }).name})</option>
                  {SELECTABLE_MODELS.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
                <EffortSelect
                  work={role}
                  label={`Thinking for ${ROLE_LABEL[role]}`}
                  draft={draftEffort}
                  setDraft={setDraftEffort}
                />
              </div>
              <span className="mrow-note">{ROLE_NOTE[role]}</span>
              <EffortClamp model={target} level={levelFor(draftEffort, role)} />
              {warn && <span className="mrow-note">{warn}</span>}
              {/* Invariant 4 in words: a routed role with no key refuses, and says
                  so here, before the pass rather than at it. */}
              {routed && lacks && (
                <span className="mrow-note">
                  {PROVIDERS[target.provider].custom
                    ? 'That endpoint has no address yet, so this won’t run. It never switches to the selected model instead.'
                    : `No ${PROVIDERS[target.provider].name} key yet, so this won’t run. It never switches to the selected model instead.`}
                </span>
              )}
            </li>
          )
        })}
        <li style={{ marginTop: 10 }}>
          <label className="mrow-head" htmlFor="effort-audit">
            {EFFORT_WORK_LABEL.audit}
          </label>
          <div className="route-pair">
            {/* No route to choose: a cross-examination runs on a model from the
                other company, picked for it, so this cell states the model
                rather than offering a choice the pass does not have. */}
            <span className="route-auto">
              {auditor ? auditor.entry.name : 'The other company’s model'}
            </span>
            <EffortSelect
              work="audit"
              id="effort-audit"
              draft={draftEffort}
              setDraft={setDraftEffort}
            />
          </div>
          <span className="mrow-note">
            A model from the other company attacks a branch, so no route applies — only how hard it
            thinks.
          </span>
          {auditor && <EffortClamp model={auditor.entry} level={levelFor(draftEffort, 'audit')} />}
        </li>
      </ul>
    </div>
  )
}

/**
 * How hard one kind of work thinks (D313): the ladder, with the level it starts
 * at marked. A draft like the route beside it, committed by the sheet's Save.
 *
 * Choosing the default clears the entry rather than setting it, so the stored
 * map holds only what somebody moved and "(default)" is true of every kind
 * nobody has touched. Every level is offered whatever the route's model is: a
 * level above a model's top runs at its top, and `EffortClamp` says so under the
 * row, rather than the list changing under somebody as they re-point the route.
 */
export function EffortSelect({
  work,
  id,
  label,
  draft,
  setDraft,
}: {
  work: EffortWork
  id?: string
  /** For a select whose visible label names the route beside it instead. */
  label?: string
  draft: EffortLevels
  setDraft: Dispatch<SetStateAction<EffortLevels>>
}) {
  return (
    <select
      id={id}
      className="select"
      aria-label={label}
      value={levelFor(draft, work)}
      onChange={(e) => {
        const level = e.target.value as Effort
        setDraft((d) => {
          const next = { ...d }
          if (level === DEFAULT_EFFORT[work]) delete next[work]
          else next[work] = level
          return next
        })
      }}
    >
      {EFFORT_LADDER.map((l) => (
        <option key={l} value={l}>
          {`${EFFORT_LABEL[l]}${l === DEFAULT_EFFORT[work] ? ' (default)' : ''}`}
        </option>
      ))}
    </select>
  )
}

/** Said under a row only when the model running it would lower its level. */
function EffortClamp({ model, level }: { model: ModelEntry; level: Effort }) {
  const note = effortNote(model, level)
  return note ? <span className="mrow-note">{note}</span> : null
}

/* ── Defaults ─────────────────────────────────────────────────────────────
   What a NEW exploration starts from. Lens, temper and tempo already persist in
   `nv.prefs`, so the live value IS the default and there is no storage work here
   at all; the four counts write through `setTuneDefaults`.

   No spend ceiling, deliberately. `runCeiling` is not persisted because a limit
   restored silently weeks later is applied to a run nobody remembers configuring,
   and a persisted DEFAULT is one refactor away from exactly that. */
export function Defaults() {
  const lens = useStore((s) => s.lens)
  const setLens = useStore((s) => s.setLens)
  const temper = useStore((s) => s.temper)
  const setTemper = useStore((s) => s.setTemper)
  const tempo = useStore((s) => s.tempo)
  const setTempo = useStore((s) => s.setTempo)
  const tune = useStore((s) => s.tuneDefaults)
  const setTune = useStore((s) => s.setTuneDefaults)
  // What these stored fields MEAN — the fallbacks live in `seededTune`, the one
  // expression of them, so the highlights here cannot drift from what boot (and
  // the composer's committed chips) will actually make of an absent field.
  const seeded = seededTune(tune)
  // Whether a cross-examination has another company's model to run on, asked the
  // way the composer asks it, so this panel never offers a count the composer
  // hides and the run would refuse. A boolean, so the answer is stable (D328).
  const canAudit = useStore((s) => runAuditor(s) !== null)
  const instruments = useStore((s) => s.instruments)
  // The rows below the fold open on arrival when the instruments are on (D300),
  // or when any of them holds something other than the shipped value — a stored
  // non-default is never put out of sight. Read once, like the splash's "Other
  // models": a disclosure that snapped shut under a click is worse than one left
  // open (D335).
  const shipped = seededTune({})
  const strayed =
    seeded.execution !== shipped.execution ||
    seeded.detail !== shipped.detail ||
    seeded.checks !== shipped.checks ||
    seeded.panels !== shipped.panels ||
    seeded.audits !== shipped.audits
  const [openMore] = useState(() => instruments || strayed)

  return (
    <>
      <div className="field">
        <span className="eyebrow">Defaults for a new exploration</span>
        <p className="gnote">
          What a new exploration starts with. You can still change each one before a run, and a
          /command overrides it.
        </p>
      </div>

      <div className="field">
        <label className="mrow-head" htmlFor="def-lens">
          Lens
        </label>
        <select
          id="def-lens"
          className="select"
          value={lens}
          onChange={(e) => setLens(e.target.value as Lens)}
        >
          {(Object.keys(LENS_LABEL) as Lens[]).map((l) => (
            <option key={l} value={l}>
              {LENS_LABEL[l]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="mrow-head" htmlFor="def-temper">
          Temper
        </label>
        <select
          id="def-temper"
          className="select"
          value={temper}
          onChange={(e) => setTemper(e.target.value as Temper)}
        >
          {(Object.keys(TEMPER_LABEL) as Temper[]).map((t) => (
            <option key={t} value={t}>
              {TEMPER_LABEL[t]}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="mrow-head" htmlFor="def-tempo">
          Pace of growth
        </label>
        <select
          id="def-tempo"
          className="select"
          value={tempo}
          onChange={(e) => setTempo(Number(e.target.value))}
        >
          {TEMPOS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.name} · {t.label}
            </option>
          ))}
        </select>
      </div>

      <TuneRow label="Moves">
        {MOVE_ROWS.map((n) => (
          <Pick key={n} on={seeded.moves === n} onPick={() => setTune({ ...tune, moves: n })}>
            {n}
          </Pick>
        ))}
      </TuneRow>

      <TuneRow label="Run mode">
        <Pick
          on={(tune.mode ?? 'auto') === 'auto'}
          onPick={() => setTune({ ...tune, mode: 'auto' })}
        >
          On its own
        </Pick>
        <Pick on={tune.mode === 'confirm'} onPick={() => setTune({ ...tune, mode: 'confirm' })}>
          Ask first
        </Pick>
      </TuneRow>

      <p className="gnote">
        Ask first holds a run between passes: each move is proposed, priced, and waits for your go.
        On its own is the run you can walk away from.
      </p>

      {/* Five rows a first visit needs; the rest of a run's shape is one tap away
          (D335). D300 puts panels and cross-examination away in Explorer mode, and
          their defaults sat at the same weight as the lens. Its head is the fold
          every other disclosure wears (`summary.cfold-head`, as the Runs tab's
          `Summary`): the chevron's one rule turned by the element's own `open`,
          and the 44px floor under a coarse pointer — a caption-height line with
          the browser's triangle was the one fold here that looked like none. */}
      <details className="field" open={openMore || undefined}>
        <summary className="cfold-head">
          <Icon name="chevron" size={9} className="cfold-chev" />
          <span className="cfold-label">More run defaults</span>
        </summary>
        <ExecutionRows tune={tune} onTune={setTune} />

        <TuneRow label="Source checks">
          {([undefined, 8, 'every'] as const).map((c) => (
            <Pick
              key={String(c)}
              on={seeded.checks === c}
              onPick={() => setTune({ ...tune, checks: c })}
            >
              {c === undefined ? 'Default' : c === 'every' ? 'Every claim' : 'More'}
            </Pick>
          ))}
        </TuneRow>

        <TuneRow label="Panels">
          {[0, 1, 3].map((n) => (
            <Pick key={n} on={seeded.panels === n} onPick={() => setTune({ ...tune, panels: n })}>
              {n === 0 ? 'None' : n}
            </Pick>
          ))}
        </TuneRow>

        {/* Without a second company's key the row is the composer's sentence
            instead — unless a count is already stored, which stays settable so
            it can be put back to None rather than hidden while still in force. */}
        {(canAudit || seeded.audits > 0) && (
          <TuneRow label="Cross-examinations">
            {[0, 1, 2].map((n) => (
              <Pick key={n} on={seeded.audits === n} onPick={() => setTune({ ...tune, audits: n })}>
                {n === 0 ? 'None' : n}
              </Pick>
            ))}
          </TuneRow>
        )}
        {!canAudit && <p className="gnote">{CROSS_EXAM_NEEDS_SECOND_KEY}</p>}
      </details>
    </>
  )
}

/**
 * The execution rows, highlighted from `seededTune` and not from the raw stored
 * field — the fallback is what the next run will actually do, and a workspace
 * that has chosen nothing showed neither chip pressed, under a note that called
 * Sequential the default, for as long as this read `tune.execution` directly.
 * Pure for the same reason as the row above.
 */
export function ExecutionRows({
  tune,
  onTune,
}: {
  tune: TuneDefaults
  onTune: (t: TuneDefaults) => void
}) {
  const seeded = seededTune(tune)
  return (
    <>
      <TuneRow label="Run execution">
        <Pick
          on={seeded.execution === 'sequential'}
          onPick={() => onTune({ ...tune, execution: 'sequential', detail: 'full' })}
        >
          Sequential
        </Pick>
        <Pick
          on={seeded.execution === 'portfolio'}
          onPick={() => onTune({ ...tune, execution: 'portfolio' })}
        >
          Portfolio
        </Pick>
      </TuneRow>

      <p className="gnote">
        Portfolio, the default, works two or three angles on a node in one request; Sequential makes
        one move per request. Progressive leaf detail is experimental.
      </p>

      {seeded.execution === 'portfolio' && (
        <TuneRow label="Portfolio leaf detail">
          <Pick on={seeded.detail === 'full'} onPick={() => onTune({ ...tune, detail: 'full' })}>
            Full
          </Pick>
          <Pick
            on={seeded.detail === 'progressive'}
            onPick={() => onTune({ ...tune, detail: 'progressive' })}
          >
            Progressive · experimental
          </Pick>
        </TuneRow>
      )}
    </>
  )
}

function TuneRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <span className="eyebrow">{label}</span>
      <div role="group" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {children}
      </div>
    </div>
  )
}

function Pick({ on, onPick, children }: { on: boolean; onPick: () => void; children: ReactNode }) {
  return (
    <button className="btchip" data-on={on || undefined} aria-pressed={on} onClick={onPick}>
      {children}
    </button>
  )
}

/* ── Appearance ───────────────────────────────────────────────────────────
   `setTheme` and never `nv.theme` directly: index.html carries a pre-paint script
   that makes the same call, and its sha256 is pinned in the CSP and asserted by
   csp.test.ts. A second writer here would be a second definition of what the
   stored value means. */
function Appearance() {
  const theme = useStore((s) => s.theme)
  const setTheme = useStore((s) => s.setTheme)
  const instruments = useStore((s) => s.instruments)
  const setInstruments = useStore((s) => s.setInstruments)
  return (
    <>
      <div className="field">
        <span className="eyebrow">Theme</span>
        <div role="radiogroup" aria-label="Theme" style={{ display: 'flex', gap: 4 }}>
          {(['dark', 'light'] as Theme[]).map((t) => (
            <button
              key={t}
              className="btchip"
              role="radio"
              aria-checked={theme === t}
              data-on={theme === t || undefined}
              onClick={() => setTheme(t)}
            >
              {t === 'dark' ? 'Dark' : 'Light'}
            </button>
          ))}
        </div>
      </div>
      {/* ONE switch for the instruments. It hides surfaces and disables nothing:
        every pass is still reachable from the palette and every export is still
        built by the same code. Off is where a fresh workspace starts. */}
      <div className="field">
        <span className="eyebrow">Instruments</span>
        <div role="radiogroup" aria-label="Instruments" style={{ display: 'flex', gap: 4 }}>
          <button
            className="btchip"
            role="radio"
            aria-checked={!instruments}
            data-on={!instruments || undefined}
            onClick={() => setInstruments(false)}
          >
            Explorer
          </button>
          <button
            className="btchip"
            role="radio"
            aria-checked={instruments}
            data-on={instruments || undefined}
            onClick={() => setInstruments(true)}
          >
            Everything
          </button>
        </div>
        <p className="gnote">
          {instruments
            ? 'The research workspace, panels and cross-examination on a claim, the compiled diagnostics, and every export format.'
            : 'The graph, the reasoning verbs, the source check and the results you record. Panels, cross-examination, the research workspace and the heavier exports are put away — each place says so, and this switch brings them back.'}
        </p>
      </div>
    </>
  )
}

/* ── Sources ──────────────────────────────────────────────────────────────
   Five rows now fetch, so the note says what leaves this machine and when. That
   sentence is the whole point of the section: a switch that sends a claim to a
   third party and does not say so is worse than no switch. The second paragraph
   is the second door — `engine/paper.ts`, reached from Sources with an
   identifier somebody typed — and it is not an aside: without it the first
   paragraph is false about arXiv, which no source check calls. Each row's state is
   RENDERED — "Connected" or "Not connected yet", never a tooltip, which a screen
   reader and a touch user both reach only by accident.

   A rejected source is ABSENT, with its reason in docs/decisions.md. Present and
   greyed would invite the next contributor to un-grey it, which for the news rows
   is a one-line defeat of invariant 2 — see the header of core/sources.ts.

   THE CITES EXCEPTION IN THE NOTE IS LOAD-BEARING, not hedging. The fetched record
   is discarded with the pass, which is what D27 chose and what makes it cost no
   corpus budget — but `resolveCites` copies the quote, the title and the notice of
   a passage the check CITED onto `verification.corpus.cites`, and that rides into
   the stored document and both exports like any other evidence. The
   sentence said "never stored" until this was found in review, which was false by
   exactly those three fields. Do not shorten it back. */
function Sources() {
  const sourcesOn = useStore((s) => s.sourcesOn)
  const setSourceOn = useStore((s) => s.setSourceOn)
  const mailto = useStore((s) => s.sourceMailto)
  const setSourceMailto = useStore((s) => s.setSourceMailto)

  return (
    <>
      <p className="gnote" id="srcNote">
        A connected row is searched during a source check, and only then. The node&rsquo;s label and
        the claim&rsquo;s main words are sent to that service; nothing else about your graph is,
        nothing is sent while &ldquo;Prefer my sources over the web&rdquo; is on, and what comes
        back is read once and not kept — except a passage the check actually cites, which is stored
        on that check like every other piece of its evidence: its quote, its title, and any
        retraction notice on it. Matching records join the same search as your own documents. The
        DOIs of the records found are then checked against Crossref&rsquo;s retraction data, in one
        batch — DOIs only, never your claim — and a retracted work is marked as one wherever it is
        shown. For at most two open-access records, Europe PMC is asked once more for the
        paper&rsquo;s own full text — its identifier and nothing else — so a quote can be checked
        against the paragraph it came from. ClinicalTrials.gov and Europe PMC get no contact
        address; neither API has a use for one.
      </p>
      {/* The second door, said here because otherwise the sentence above is
          false about one row: arXiv is never searched during a check — it has no
          adapter in engine/liveSources.ts and `enabledLiveRows` will not hand it
          to a gather — and a switch that governs nothing is decoration on a
          settings page. What it governs is this. */}
      <p className="gnote">
        Four of these rows are also reached from <b>{SOURCES_DOOR}</b>, where typing a DOI or an
        arXiv id fetches that one paper into your documents: Crossref resolves the DOI, arXiv
        resolves the id, and OpenAlex and Europe PMC fill in an abstract or the open-access full
        text. Only the identifier you typed is sent, never your graph. A row switched off here is
        not called there either, and arXiv is called nowhere else at all.
      </p>

      {SOURCE_CATEGORIES.map((cat) => {
        const rows = SOURCES.filter((s) => s.category === cat)
        if (rows.length === 0) return null
        return (
          <div
            key={cat}
            className="field"
            role="group"
            aria-label={SOURCE_CATEGORY_LABEL[cat]}
            style={{ gap: 6 }}
          >
            <span className="eyebrow">{SOURCE_CATEGORY_LABEL[cat]}</span>
            {rows.map((s) => {
              // The store's record is SPARSE on purpose, so an id it does not
              // mention takes the registry's own default. Reading it as
              // `=== true` would render every row off until it was touched once.
              const on = sourcesOn[s.id] ?? s.defaultOn
              return (
                <button
                  key={s.id}
                  role="switch"
                  aria-checked={on}
                  aria-describedby="srcNote"
                  onClick={() => setSourceOn(s.id, !on)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    width: '100%',
                    padding: '6px 0',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span className="mrow-head">{s.name}</span>
                    <span className="mrow-note">{s.blurb}</span>
                    <span className="mrow-lack">{s.wired ? 'Connected' : 'Not connected yet'}</span>
                  </span>
                  <span className="switch" data-on={on} style={{ marginTop: 2 }} />
                </button>
              )
            })}
          </div>
        )
      })}

      <div className="field">
        <label className="mrow-head" htmlFor="srcMailto">
          Contact email (optional)
        </label>
        <input
          id="srcMailto"
          className="input"
          type="email"
          autoComplete="off"
          spellCheck={false}
          placeholder="you@example.com"
          value={mailto}
          onChange={(e) => setSourceMailto(e.target.value)}
        />
        <p className="gnote">
          Sent with requests to the polite pools at OpenAlex and Crossref so they can reach you
          about traffic. It identifies you; it authorises nothing. Not a secret, not sent anywhere
          else, and left blank the requests still go — just in the slower queue.
        </p>
      </div>
    </>
  )
}
